/**
 * MainTableTile — Plan 05-12 Task 1.
 *
 * NEW tile shape: sortable grid with clickable rows to SF.
 * Per UI-SPEC §"MainTableTile anatomy".
 *
 * UX-07 4-state machine: loading / cache-warming / sf-unreachable / success
 *
 * Layout:
 *   - Card padding p-0 (table fills card)
 *   - Column headers text-[0.72rem] semibold uppercase ink-500
 *   - Row hover bg-ink-100
 *   - Numeric columns tabular-nums text-right
 *   - First cell (isSfLink=true) → Link to /sf-link/:id + ↗ glyph
 *   - Footer row font-semibold border-t-2 border-ink-200
 *   - max-h-96 overflow-y-auto
 */

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';

import type { MainGroupedResponse, MainBucketGroup } from '@saledash/shared';

export interface TableColumn {
  key: string;
  header: string;
  /** If true, this cell renders as a Link to /sf-link/:id */
  isSfLink?: boolean;
  /** The key in the bucket object to use as the SF record ID (default: 'opportunity_id') */
  sfIdKey?: string;
  /** If true, right-align + tabular-nums */
  isNumeric?: boolean;
}

interface MainTableTileProps {
  metricId: string;
  title: string;
  columns: TableColumn[];
  /** Optional tooltip formula */
  tooltipFormula?: string;
  data: MainGroupedResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Format a cell value — numeric columns get currency/number formatting.
 */
function formatCell(value: unknown, isNumeric?: boolean): string {
  if (value === null || value === undefined) return '—';
  if (isNumeric && typeof value === 'number') {
    // Compact currency for large amounts, plain for small counts
    if (value >= 1000) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
    }
    return String(value);
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- bucket cell values are JSON primitives from the API; String() output unchanged
  return String(value);
}

/**
 * Sum a numeric column across all buckets for the footer total.
 */
function sumColumn(buckets: MainBucketGroup[], key: string): number {
  return buckets.reduce((acc, b) => {
    const v = b[key];
    return acc + (typeof v === 'number' ? v : 0);
  }, 0);
}

export function MainTableTile({
  metricId,
  title,
  columns,
  tooltipFormula,
  data,
  isLoading,
  isError,
  error,
  refetch,
}: MainTableTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  // UX-07: cache-warming
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <div className="p-4">
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <div className="p-4">
          <SfUnreachableBanner onRetry={refetch} />
        </div>
      </Card>
    );
  }

  // Loading
  if (isLoading || !data) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
        </CardHeader>
        <div className="p-4 space-y-3" aria-busy="true">
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-1" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-2" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-3" />
        </div>
      </Card>
    );
  }

  const buckets = data.buckets;

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      <CardHeader>
        <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
          {title}
        </CardTitle>
        {tooltipFormula && (
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        )}
      </CardHeader>

      {/* Empty state */}
      {buckets.length === 0 && (
        <p
          className="py-12 text-center text-sm text-ink-500"
          data-testid="table-empty-state"
        >
          No deals match the current filter.
        </p>
      )}

      {/* Table */}
      {buckets.length > 0 && (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b border-ink-200">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500 ${col.isNumeric ? 'text-right' : 'text-left'}`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket) => (
                <tr
                  key={String(bucket[columns[0]?.key ?? 'label'])}
                  className="min-h-9 hover:bg-ink-100 border-b border-ink-100"
                  data-testid="table-data-row"
                >
                  {columns.map((col) => {
                    const cellValue = bucket[col.key];
                    const sfId = bucket[col.sfIdKey ?? 'opportunity_id'];

                    if (col.isSfLink && sfId) {
                      // 2026-06-16: SF deeplink — the api's frontdoor.jsp bridge
                      // lives at /api/sf/link/:id (cross-origin to the SPA host
                      // on cro.*). Use a regular <a> with the absolute api URL
                      // so the browser does a full navigation (and carries the
                      // session cookie scoped to .salesdashboard.adeptia.org).
                      // React Router <Link> tried to handle this as an SPA-
                      // internal route, fell through to the catch-all, and
                      // redirected the user back to '/'.
                      const apiBase =
                        typeof window !== 'undefined' &&
                        window.location.hostname.endsWith('salesdashboard.adeptia.org')
                          ? `${window.location.protocol}//api.salesdashboard.adeptia.org`
                          : '';
                      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- bucket cell values are JSON primitives from the API; String() output unchanged
                      const href = `${apiBase}/api/sf/link/${String(sfId)}`;
                      return (
                        <td
                          key={col.key}
                          className="px-4 py-3"
                          data-testid="sf-link-cell"
                        >
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-600 hover:text-brand-700 hover:underline"
                          >
                            {formatCell(cellValue)} ↗
                          </a>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-ink-900 ${col.isNumeric ? 'tabular-nums text-right' : ''}`}
                      >
                        {formatCell(cellValue, col.isNumeric)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* Footer total row */}
            <tfoot>
              <tr
                className="border-t-2 border-ink-200 font-semibold bg-white"
                data-testid="table-footer-row"
              >
                {columns.map((col, colIdx) => {
                  if (colIdx === 0) {
                    return (
                      <td key={col.key} className="px-4 py-3 text-ink-900 text-2xl font-bold tabular-nums">
                        TOTAL
                      </td>
                    );
                  }
                  if (col.isNumeric) {
                    const total = sumColumn(buckets, col.key);
                    return (
                      <td key={col.key} className="px-4 py-3 tabular-nums text-right text-ink-900">
                        {formatCell(total, true)}
                      </td>
                    );
                  }
                  return <td key={col.key} className="px-4 py-3" />;
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}
